import type { KeyboardEvent } from 'react';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useRefinementList } from 'react-instantsearch';

import { Flex } from '@/components/Flex';
import type { ExtractedCollectionData } from '@/components/HeroProductListing/helpers';
import { sortAndFilterRefinementItems } from '@/components/ListingFilters/components/WatchCollectionFilter/helpers';

import watchAvenger from '@/assets/watches/avenger.png';
import watchChronomat from '@/assets/watches/chronomat.png';
import watchClassicAvi from '@/assets/watches/classic-avi.png';
import watchNavitimer from '@/assets/watches/navitimer.png';
import watchPremier from '@/assets/watches/premier.png';
import watchProfessional from '@/assets/watches/professional.png';
import watchSuperocean from '@/assets/watches/superocean.png';
import watchSuperoceanHeritage from '@/assets/watches/superocean-heritage.png';
import watchToptime from '@/assets/watches/toptime.png';

import * as S from './WatchCollectionFilter.styles';

const WATCH_PHOTO_MAPPING: Record<string, string> = {
  'Classic AVI': watchClassicAvi.src,
  'Superocean Heritage': watchSuperoceanHeritage.src,
  Avenger: watchAvenger.src,
  Chronomat: watchChronomat.src,
  Colt: '',
  Galactic: '',
  Navitimer: watchNavitimer.src,
  Premier: watchPremier.src,
  Professional: watchProfessional.src,
  Superocean: watchSuperocean.src,
  'Top Time': watchToptime.src,
};

export interface WatchCollectionFilterProps
  extends Omit<UseRefinementListProps, 'attribute'> {
  collectionData: ExtractedCollectionData;
  isLoading: boolean;
}

export const WatchCollectionFilter = ({
  collectionData,
  isLoading,
  ...props
}: WatchCollectionFilterProps) => {
  const { items, refine } = useRefinementList({
    attribute: 'collection',
    ...props,
  });
  const collectionNames = collectionData.map(({ name }) => name);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, value: string) => {
    if (event.key === ' ' || event.key === 'Enter') {
      refine(value);
    }
  };

  if (isLoading) return null;

  return (
    <Flex data-testid='WatchCollectionFilter' justifyContent='center'>
      <S.WatchesGrid gridGap={1}>
        {sortAndFilterRefinementItems(items, collectionNames).map(
          ({ isRefined, value }, index) => {
            const collection = collectionData.find(
              ({ name }) => name === value,
            );

            return (
              <S.WatchTypeSelection
                key={value}
                alignItems='end'
                backgroundURL={collection?.image ?? WATCH_PHOTO_MAPPING[value]}
                data-testid={`WatchCollectionFilter-WatchTypeSelection-${index}`}
                isRefined={isRefined}
                justifyContent='center'
                onClick={() => refine(value)}
                onKeyDown={(e) => handleKeyDown(e, value)}
                tabIndex={index + 1}
              >
                <S.Background
                  data-testid={`WatchCollectionFilter-Background-${index}`}
                />

                <S.WatchName
                  data-testid={`WatchCollectionFilter-WatchName-${index}`}
                >
                  {value}
                </S.WatchName>
              </S.WatchTypeSelection>
            );
          },
        )}
      </S.WatchesGrid>
    </Flex>
  );
};

WatchCollectionFilter.displayName = 'WatchCollectionFilter';
